import { useState, useEffect, useCallback } from "react";
import {
  MenuItem,
  Select,
  Avatar,
  Button,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import {
  Send,
  Refresh,
} from "@mui/icons-material";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import CustomSnackbar from "../Common/snackBar";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import {
  getOrganizations,
  sendInvoiceAction,
  updateInvoiceStatus
} from "../../Actions/OrganizationAction";
import { listTripolaTransactionDataAction } from "../../Actions/TripolaPlanActions";
import DataTable from "../../components/DataTable/DataTable";

const MerchantInvoiceSection = () => {
  const [organizations, setOrganizations] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("paycentral"); // New state
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("all");
  const [appliedPlatform, setAppliedPlatform] = useState("paycentral"); // New state
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [filterButtonDisabled, setFilterButtonDisabled] = useState(true);
  const [resetButtonDisabled, setResetButtonDisabled] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let res;

      if (appliedPlatform === "tripola") {
        // Call Tripola API
        res = await listTripolaTransactionDataAction(
          selectedRowsPerPage,
          currentPage,
          appliedSearch || "all",
          null,
          null,
          appliedStatus !== "all" ? appliedStatus : "all",
          null,
          "all"
        );
        setOrganizations(res.data.data);
        setTotalRecords(res.data.totalCount || 0);
      } else {
        // Call Paycentral API (original)
        res = await getOrganizations(
          currentPage,
          appliedSearch,
          selectedRowsPerPage,
          appliedStatus !== "all" ? appliedStatus : ""
        );
        setOrganizations(res.data.data);
        setTotalRecords(res.data.totalCount || 0);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to fetch ${appliedPlatform} data`,
        severity: "error",
      });
    }
    setLoading(false);
  }, [currentPage, appliedSearch, appliedStatus, appliedPlatform, selectedRowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData, currentPage, selectedRowsPerPage]);

  const updateFilterButtonState = (search, status, platform) => {
    setFilterButtonDisabled(
      search === appliedSearch &&
      status === appliedStatus &&
      platform === appliedPlatform
    );
  };

  const handleSend = async (org) => {
    setSendingId(org.id);
    try {
      await sendInvoiceAction(org.id);
      setSnackbar({
        open: true,
        message: org.isAutoInvoice ? "Invoice resent successfully" : "Invoice sent successfully",
        severity: "success"
      });
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to send invoice", severity: "error" });
    }
    setSendingId(null);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateInvoiceStatus(id, status);
      fetchData(); // refresh table
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    updateFilterButtonState(searchValue, statusFilter, platformFilter);
  }, [searchValue, statusFilter, platformFilter, appliedSearch, appliedStatus, appliedPlatform]);

  // Paycentral columns (original)
  const paycentralColumns = [
    {
      field: "organizationName",
      headerName: "Organization",
      flex: 1,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: "primary.main",
              color: "white",
              fontSize: 14,
            }}
          >
            {row.organizationName?.charAt(0)}
          </Avatar>

          <Typography fontWeight={500} ml={1}>
            {row.organizationName}
          </Typography>
        </Box>
      ),
    },

    { field: "email", headerName: "Email Address", flex: 1 },

    {
      field: "totalFee",
      headerName: "Total Fee",
      flex: 1,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => {
        const value = Number(row.totalFee || 0);

        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              height: "100%",
              pr: 2,
            }}
          >
            <Typography fontWeight={600} sx={{ fontSize: "16px", color: "#1a1a1a" }}>
              {value.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
        );
      },
    },

    {
      field: "status",
      headerName: "Status",
      flex: 0.6,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        const { status, id } = row;

        return (
          <Select
            size="small"
            value={status || "PENDING"}
            disabled={status === "SUCCESS"}
            onChange={(e) => handleStatusChange(id, e.target.value)}
            sx={{
              minWidth: 120,
              height: 35,
              backgroundColor: status === "SUCCESS" ? "#e6f4ea" : "",
            }}
          >
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="SUCCESS">Success</MenuItem>
          </Select>
        );
      },
    },

    {
      field: "isAutoInvoice",
      headerName: "Billing Status",
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => (
        <Chip
          label={
            row.isAutoInvoice && row.isEmailSend
              ? "Auto Invoice"
              : !row.isAutoInvoice && !row.isEmailSend
                ? "Send"
                : "Manual"
          }
          size="small"
          color={
            row.isAutoInvoice && row.isEmailSend ? "success" : "default"
          }
          sx={{ fontWeight: 500, minWidth: 110 }}
        />
      ),
    },

    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      align: "right",
      headerAlign: "right",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const { isAutoInvoice, isEmailSend, id } = row;

        const showResend = isEmailSend || (isAutoInvoice && !isEmailSend);
        const isSending = sendingId === id;

        return (
          <Button
            variant={showResend ? "outlined" : "contained"}
            size="small"
            startIcon={
              isSending ? (
                <CircularProgress size={16} />
              ) : showResend ? (
                <Refresh />
              ) : (
                <Send />
              )
            }
            disabled={isSending}
            onClick={(e) => {
              e.stopPropagation();
              handleSend(row);
            }}
            sx={{ borderRadius: 2, textTransform: "none", px: 2.5 }}
          >
            {isSending ? "Sending..." : showResend ? "Resend" : "Send"}
          </Button>
        );
      },
    },
  ];

  // Tripola columns
  const tripolaColumns = [
    {
      field: "tenantName",
      headerName: "Tenant Name",
      flex: 1,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: "primary.main",
              color: "white",
              fontSize: 14,
            }}
          >
            {row.tenantName?.charAt(0) || "T"}
          </Avatar>

          <Typography fontWeight={500} ml={1}>
            {row.tenantName}
          </Typography>
        </Box>
      ),
    },

    { field: "tenantEmail", headerName: "Tenant Email", flex: 1 },

    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => {
        const value = Number(row.amount || 0);

        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              height: "100%",
              pr: 2,
            }}
          >
            <Typography fontWeight={600} sx={{ fontSize: "16px", color: "#1a1a1a" }}>
              {value.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
        );
      },
    },

    {
      field: "status",
      headerName: "Status",
      flex: 0.6,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        const getStatusColor = (status) => {
          switch (status?.toLowerCase()) {
            case "success":
              return "success";
            case "pending":
              return "warning";
            case "failed":
              return "error";
            case "refunded":
              return "info";
            default:
              return "default";
          }
        };

        return (
          <Chip
            label={
              row.status?.charAt(0).toUpperCase() + row.status?.slice(1) ||
              "Unknown"
            }
            size="small"
            color={getStatusColor(row.status)}
            sx={{ fontWeight: 500, minWidth: 80 }}
          />
        );
      },
    },

    {
      field: "billingStatus",
      headerName: "Billing Status",
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      renderCell: () => (
        <Chip
          label="Auto Invoice"
          size="small"
          color="success"
          sx={{ fontWeight: 500, minWidth: 110 }}
        />
      ),
    },
  ];

  // Choose columns based on platform
  const columns = appliedPlatform === "tripola" ? tripolaColumns : paycentralColumns;

  return (
    <div id="kt_content_container">
      <BreadcrumbsComponent />

      <DataTable
        rows={organizations}
        columns={columns}
        loading={loading}
        totalCount={totalRecords}
        page={currentPage}
        pageSize={selectedRowsPerPage}
        getRowId={(row) => row.id}
        onPageChange={(page) => setCurrentPage(page)}
        onPageSizeChange={(size) => {
          setSelectedRowsPerPage(size);
          setCurrentPage(1);
        }}

        filterValues={{
          search: searchValue,
          status: statusFilter,
          platform: platformFilter,
        }}

        onFilterApply={(values) => {
          setSearchValue(values.search || "");
          setStatusFilter(values.status || "all");
          setPlatformFilter(values.platform || "paycentral");

          setAppliedSearch(values.search || "");
          setAppliedStatus(values.status || "all");
          setAppliedPlatform(values.platform || "paycentral");

          setCurrentPage(1);
        }}

        onFilterReset={() => {
          setSearchValue("");
          setStatusFilter("all");
          setPlatformFilter("paycentral");

          setAppliedSearch("");
          setAppliedStatus("all");
          setAppliedPlatform("paycentral");

          setCurrentPage(1);
          setSelectedRows([]);
        }}

        filters={[
          {
            type: "search",
            placeholder: "Search organization...",
          },
          {
            type: "select",
            name: "status",
            options: [
              { label: "All Status", value: "all" },
              { label: "Success", value: "success" },
              { label: "Pending", value: "pending" },
              ...(platformFilter === "tripola"
                ? [
                  { label: "Failed", value: "failed" },
                  { label: "Refunded", value: "refunded" },
                ]
                : []),
            ],
          },
          {
            type: "select",
            name: "platform",
            options: [
              { label: "Paycentral", value: "paycentral" },
              { label: "Tripola", value: "tripola" },
            ],
          },
        ]}

        onAction={(action, row) => {
          if (action === "send") handleSend(row);
        }}

        bulkActions={{
          delete: false,
        }}

        onAddClick={null}
        hasCreatePermission={false}
      />

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default MerchantInvoiceSection;